import { useCallback, useEffect, useState } from "react";
import { Monthly } from "../../api/services/next";

/**
 * HS-70186: which Monthly View flow to render - "legacy" (GetMonthlyData, today's
 * exact code path) or "grid" (GetMonthlyGrid, server-built cells). Server-gated via
 * MonthlyView:UseMonthlyGrid (Monthly/GetSettings) - the flag is the source of truth,
 * this hook only decides how to react to it:
 *   - loading/error/false -> "legacy"
 *   - no answer within SETTINGS_TIMEOUT_MS -> "legacy" (the search waits on
 *     settingsLoaded, so a stalled call must not leave the page blank)
 *   - true -> "grid", unless ?monthlyFlow=legacy overrides it (QA escape hatch)
 *   - ?monthlyFlow=grid never forces grid on when the server flag is off - the grid
 *     endpoints 404 in that case and onGridNotFound() below falls back anyway.
 *
 * Perf: the last server answer is kept in localStorage, so a returning user's first
 * search starts on mount instead of waiting a GetSettings round trip. GetSettings still
 * runs and wins: a changed answer switches the flow (index.jsx re-searches on a flow
 * change), and a stale "true" is caught by the grid endpoints' 404 -> onGridNotFound.
 * With nothing cached (first visit) the hook waits for GetSettings as before.
 */
const SETTINGS_TIMEOUT_MS = 5000;
export const FLOW_CACHE_KEY = "monthlyView.useMonthlyGrid";

const readCachedFlag = () => {
  try {
    const v = window.localStorage.getItem(FLOW_CACHE_KEY);
    return v === "true" ? true : v === "false" ? false : null;
  } catch {
    return null;
  }
};

const writeCachedFlag = (value) => {
  try {
    window.localStorage.setItem(FLOW_CACHE_KEY, String(value));
  } catch {
    // storage unavailable (private mode / quota) - just no fast path next time
  }
};

const readOverride = () => {
  try {
    return new URLSearchParams(window.location.search).get("monthlyFlow");
  } catch {
    return null;
  }
};

const flowFor = (serverGridEnabled, override) =>
  serverGridEnabled && override !== "legacy" ? "grid" : "legacy";

export const useMonthlyFlow = () => {
  const [override] = useState(readOverride);
  const [cached] = useState(readCachedFlag);
  const [flow, setFlow] = useState(() => flowFor(cached === true, override));
  const [settingsLoaded, setSettingsLoaded] = useState(cached !== null);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(`GetSettings timed out after ${SETTINGS_TIMEOUT_MS} ms`)), SETTINGS_TIMEOUT_MS);
    });
    Promise.race([Monthly.GetMonthlySettings(), timeout])
      .then(settings => {
        if (cancelled) return;
        const serverGridEnabled = !!settings?.UseMonthlyGrid;
        writeCachedFlag(serverGridEnabled);
        setFlow(flowFor(serverGridEnabled, override));
        setSettingsLoaded(true);
      })
      .catch(err => {
        // Settings fetch failed (network error, old gateway without the endpoint, etc)
        // - fall back to legacy rather than risk a blank grid, but leave a trace so a
        // broken GetSettings isn't indistinguishable from the flag being off. A cached
        // flow is kept: a wrong "grid" still falls back through the grid 404.
        console.warn("MonthlyView: GetSettings failed, using legacy flow", err);
        if (!cancelled) {
          if (cached === null) setFlow("legacy");
          setSettingsLoaded(true);
        }
      })
      .finally(() => clearTimeout(timer));
    return () => { cancelled = true; clearTimeout(timer); };
  }, [override, cached]);

  // A 404 from the grid endpoints themselves (flag flipped off mid-session, or a
  // gateway that hasn't deployed the new controller yet) resets to legacy.
  const onGridNotFound = useCallback(() => {
    writeCachedFlag(false);
    setFlow("legacy");
  }, []);

  return { flow, settingsLoaded, onGridNotFound };
};
